/**
 * Per-user search term for the projects grid, remembered for the tab's lifetime.
 *
 * WHY THIS EXISTS
 * `/projects` and `/projects/:id` are separate views: Projects.jsx swaps
 * `currentView`, so ProjectsList unmounts on a card click and remounts on the way
 * back (see the comment above `activityMap` there). Every filter the person had
 * applied died with it, so narrowing a long list, opening one project and coming
 * back meant retyping the search every single time.
 *
 * WHY sessionStorage AND NOT localStorage
 * A search term is a working-set filter, not a preference. Surviving the tab is
 * what makes the round-trip painless; surviving a browser restart would mean
 * someone opens the app days later to a grid that silently hides most of their
 * projects with no memory of why. [[projectPins]] is the opposite case — a
 * deliberate, permanent choice — and correctly uses localStorage.
 *
 * WHY NOT THE URL
 * `?q=` would be shareable and Back-friendly, but it has to be threaded through
 * every navigation that leaves this screen. Revisit if the list ever needs to be
 * linkable; this layer would be the thing replaced.
 *
 * WHY A SEPARATE KEY FROM projectsListCache
 * That key holds the fetched project list and is rewritten wholesale on every
 * refresh. Folding view state into a data cache would tie the filter's lifetime
 * to cache invalidation, which has nothing to do with it.
 *
 * SCOPE
 * Search text only. The type filter and sort stay transient by design — see the
 * comment on `selectedTypes` in ProjectsList.jsx.
 */

/**
 * Per-email key, matching the `projectsListCache:<email>` convention used by the
 * same screen. One key per user rather than one shared map: a tab only ever needs
 * its own user's term, and this way nothing has to parse another account's state.
 *
 * @returns {string}
 */
export const searchTermKey = (email) => `projectsListSearch:${email}`;

/**
 * @returns {string} the stored term for this user, or '' when there is none.
 *   Always a string; never throws. Consumed by a `useState` initializer, so it
 *   must be safe to call during render.
 */
export const readSearchTerm = (email) => {
  try {
    return sessionStorage.getItem(searchTermKey(email)) ?? '';
  } catch (error) {
    // Storage blocked (Safari private mode, hardened profiles). An unfiltered
    // grid is a fine degradation; throwing into render is not.
    console.warn('projectListSearch: could not read term, ignoring', error);
    return '';
  }
};

/**
 * Persist the term, or remove the key when the box is empty.
 *
 * Removing rather than storing '' keeps the tab's storage free of dead keys for
 * every user who ever typed and then cleared the box.
 *
 * A non-string is ignored outright — storing one would round-trip through
 * `sessionStorage`'s implicit String() and come back as the literal "undefined",
 * which would then be applied as a filter and hide every project.
 */
export const writeSearchTerm = (email, term) => {
  const key = searchTermKey(email);
  try {
    if (typeof term !== 'string' || term.trim() === '') {
      sessionStorage.removeItem(key);
      return;
    }
    // Stored verbatim, not trimmed: the filter matches on the raw value, so
    // trimming here would make the restored grid disagree with the input box.
    sessionStorage.setItem(key, term);
  } catch (error) {
    console.warn('projectListSearch: could not persist term', error);
  }
};
